import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsNotificationsV2NotificationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/events-notifications',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-notifications',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-notifications',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events-notifications',
        destPath: '',
      },
      {
        srcPath: '/events/v2/notifications',
        destPath: '/v2/notifications',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixEventsNotificationsV2NotificationConfigManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/events-notifications',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-notifications',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-notifications',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events-notifications',
        destPath: '',
      },
      {
        srcPath: '/events/v2/notifications',
        destPath: '/v2/notifications',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_notifications';

/** Resolves a NotificationConfig by id. Returns saved value or default value if not saved yet. */
export function resolveNotificationConfig(
  payload: object
): RequestOptionsFactory<any> {
  function __resolveNotificationConfig({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.notifications.v2.notification_config',
      method: 'GET' as any,
      methodFqn:
        'wix.events.notifications.v2.NotificationConfigManagement.ResolveNotificationConfig',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsNotificationsV2NotificationConfigManagementUrl({
        protoPath: '/v2/notification-configs/resolve/{notificationConfigId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'notificationConfig.createdDate' },
              { path: 'notificationConfig.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __resolveNotificationConfig;
}

/** Upsert a NotificationConfig */
export function upsertNotificationConfig(
  payload: object
): RequestOptionsFactory<any> {
  function __upsertNotificationConfig({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'notificationConfig.createdDate' },
          { path: 'notificationConfig.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.notifications.v2.notification_config',
      method: 'POST' as any,
      methodFqn:
        'wix.events.notifications.v2.NotificationConfigManagement.UpsertNotificationConfig',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsNotificationsV2NotificationConfigManagementUrl({
        protoPath: '/v2/notification-configs/{notificationConfig.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'notificationConfig.createdDate' },
              { path: 'notificationConfig.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __upsertNotificationConfig;
}

/** Triggers notification */
export function triggerNotification(
  payload: object
): RequestOptionsFactory<any> {
  function __triggerNotification({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.notifications.v2.notification_config',
      method: 'POST' as any,
      methodFqn:
        'wix.events.notifications.v2.NotificationService.TriggerNotification',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsNotificationsV2NotificationServiceUrl({
        protoPath: '/v2/notifications/trigger',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __triggerNotification;
}
